import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { RefreshCw } from 'lucide-react'
import { useMenuStore } from '@/store/useMenuStore.ts'
import { useProjectStageStore } from '@/components/common/menu/lib/store/useProjectStageStore.tsx'
import { cn } from '@/lib/utils.ts'
import { useAnimateQueueCount } from '@/components/common/menu/lib/hooks/useAnimateQueueCount.ts'

export default function NavigationMobileCountBtn() {
  const closeMobileMenu = useMenuStore(state => state.closeMobileMenu)
  const { t } = useTranslation()
  const stageIri = useProjectStageStore(state => state.iri)
  const { isAnimating, queueCount } = useAnimateQueueCount()

  if (queueCount === 0) return null

  // Compact icon-button matching the back button's visual weight
  // (same size, border, shadow, rounding). The previous layout used
  // `w-14 pl-2` which left a large dead-space gutter on the right
  // of the icon and pushed the count badge OFF the viewport when
  // the button sat flush against the right edge. Now: square 32px
  // button with the count badge inset at the top-right corner so
  // the chip stays fully on-screen regardless of placement.
  //
  // Tooltip dropped — was only meaningful for desktop hover, and
  // wrapping the NavLink with the Tip span hijacked touch events
  // on mobile (TooltipTrigger.asChild captured onTouchStart before
  // the link navigated). The aria-label carries the same info for
  // screen readers without the touch-handling collision.
  return (
    <NavLink
      onClick={closeMobileMenu}
      to={`${stageIri}/sync-queue`}
      aria-label={`${t('navigation.queueTip')} ${queueCount}`}
      className={cn(
        `relative flex size-8 shrink-0 items-center justify-center rounded-lg border bg-background text-foreground shadow-sm active:scale-95`,
      )}
    >
      <RefreshCw
        className={cn(
          'size-3.5 transition-colors',
          isAnimating && 'text-destructive',
          isAnimating && 'animate-[spin_1500ms_linear_infinite]',
        )}
      />
      <span
        className={cn(
          // Badge inset onto the button's top-right corner — fully
          // inside the button's bounding rect so the chip stays
          // visible no matter how tight the topbar layout is.
          `absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-muted-foreground px-1 text-[10px] font-bold leading-none text-white shadow ring-1 ring-background`,
          isAnimating && 'bg-destructive',
        )}
      >
        {queueCount}
      </span>
    </NavLink>
  )
}
